// Horizontal panning for BucketSankeyChart. Pure functions -- no DOM, no React.
//
// The chart box is widthGrowth x the viewport wide. widthGrowth is 1 (fill the card,
// no panning) unless the columns need more room than the viewport has: adjacent
// column centres must be at least `minColumnGapPx` apart on screen, and the last
// column's count labels must fit at the right edge. The box is then wider than the
// viewport and translated by `panX` (px, <= 0; 0 = left end) inside it.

import { CANVAS, HOST_BOX, NODE_WIDTH, SPACING, VIEW_W } from "./sankeyTokens";
import { COUNT_LABEL_GAP, COUNT_LABEL_EDGE_PX } from "./sankeyLabels";

/** Share of the box width between the first and the last column's bar (left edges). */
export const COLUMN_SPAN_FRACTION = (VIEW_W - NODE_WIDTH) / CANVAS.W;

/** Canvas units to the right of the last column's labels' left edge. */
export const RIGHT_LABEL_ROOM_UNITS = CANVAS.W - (HOST_BOX.left + SPACING + VIEW_W + COUNT_LABEL_GAP);

/**
 * Box width (px) the columns need: (columnCount - 1) gaps of minColumnGapPx across the
 * column span (the rest of the box is the first / last column margins, which scale
 * with it), and enough right margin for the last column's widest label.
 */
export function computeRequiredWidthPx({ columnCount = 0, minColumnGapPx = 0, lastLabelWidthPx = 0 } = {}) {
  const span = columnCount > 1 && minColumnGapPx > 0
    ? ((columnCount - 1) * minColumnGapPx) / COLUMN_SPAN_FRACTION
    : 0;
  const label = lastLabelWidthPx > 0
    ? ((lastLabelWidthPx + COUNT_LABEL_EDGE_PX) * CANVAS.W) / RIGHT_LABEL_ROOM_UNITS
    : 0;
  return Math.max(span, label);
}

/** Box width / viewport width: 1 when everything fits (or the viewport is unknown). */
export function computeWidthGrowth(requiredWidthPx, viewportWidthPx) {
  if (!(viewportWidthPx > 0) || !(requiredWidthPx > 0)) return 1;
  return Math.max(1, requiredWidthPx / viewportWidthPx);
}

/**
 * Pan limits (px). maxPan shows the left end (content left edge, less edgePad, at the
 * viewport's left edge); minPan the right end. With no overflow both are 0.
 *
 * @param {{ viewportW:number, boxW:number, contentL:number, contentR:number, edgePad?:number }} args
 *        contentL / contentR: content bounds in px from the box's left edge
 */
export function getPanBounds({ viewportW, boxW, contentL, contentR, edgePad = 0 }) {
  if (!(viewportW > 0) || !(boxW > viewportW + 0.5)) return { minPan: 0, maxPan: 0, pannable: false };
  const maxPan = Math.min(0, -(contentL - edgePad)) + 0; // + 0: never -0
  const minPan = Math.max(viewportW - boxW, viewportW - (contentR + edgePad));
  if (minPan >= maxPan) return { minPan: maxPan, maxPan, pannable: false };
  return { minPan, maxPan, pannable: true };
}

/** Keeps a pan value inside the bounds. */
export function clampPanValue(raw, { minPan, maxPan }) {
  const value = Number.isFinite(raw) ? raw : minPan;
  return Math.max(minPan, Math.min(maxPan, value));
}

/** Starting pan: the right end, so the most recent columns are in view. */
export const initialPan = (bounds) => bounds.minPan;

/**
 * Horizontal scroll intent of a wheel event, px (positive = towards the right end), or
 * 0 to leave the event alone. Trackpad horizontal swipes (deltaX dominant) and
 * shift + wheel pan; a plain vertical wheel keeps scrolling the page.
 * deltaMode 1 (lines) is converted at 16px per line, 2 (pages) at `pagePx`.
 */
export function wheelPanDelta({ deltaX = 0, deltaY = 0, shiftKey = false, deltaMode = 0 } = {}, pagePx = 800) {
  const unit = deltaMode === 1 ? 16 : deltaMode === 2 ? pagePx : 1;
  if (shiftKey) return (deltaY || deltaX) * unit;
  if (Math.abs(deltaX) > Math.abs(deltaY)) return deltaX * unit;
  return 0;
}

/** Which edges hide content (show a fade there). */
export function getEdgeFades(panX, { minPan, maxPan, pannable }) {
  if (!pannable) return { left: false, right: false };
  return { left: panX < maxPan - 1, right: panX > minPan + 1 };
}

/** Pan change for an arrow key: one column towards the arrow, else 0. */
export function arrowPanStep(key, columnPitchPx) {
  if (!(columnPitchPx > 0)) return 0;
  if (key === "ArrowLeft") return columnPitchPx;
  if (key === "ArrowRight") return -columnPitchPx;
  return 0;
}

/**
 * Pan that brings a point `xPx` (px from the box's left edge) into view with `marginPx`
 * to spare, moving as little as possible; unchanged when it is already in view.
 * Used when keyboard focus moves to a node or ribbon in a hidden column.
 */
export function panToReveal({ panX, xPx, viewportW, bounds, marginPx = 0 }) {
  if (!bounds?.pannable || !Number.isFinite(xPx) || !(viewportW > 0)) return panX;
  const onScreen = xPx + panX;
  let next = panX;
  if (onScreen < marginPx) next = marginPx - xPx;
  else if (onScreen > viewportW - marginPx) next = viewportW - marginPx - xPx;
  return clampPanValue(next, bounds);
}
